/**
 * Intercepteur pour gérer le refresh automatique des tokens JWT
 * Rafraîchit le token avant expiration et gère les erreurs 401
 */
import storage from '../utils/storage';
import Logger from '../utils/logger';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  isRefreshing = false;
  failedQueue = [];
};

/**
 * Vérifie si le token va expirer dans les X minutes
 * @param {number} minutesBeforeExpiry - Nombre de minutes avant expiration
 * @returns {boolean}
 */
export const shouldRefreshToken = (minutesBeforeExpiry = 2) => {
  try {
    const token = storage.getToken();
    if (!token) return false;

    const decoded = storage.decodeToken();
    if (!decoded || !decoded.exp) return false;

    const expirationTime = decoded.exp * 1000; // Convertir en millisecondes
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const minutesUntilExpiry = timeUntilExpiry / (1000 * 60);

    return minutesUntilExpiry < minutesBeforeExpiry;
  } catch (error) {
    Logger.error('[TokenRefresh] Erreur lors de la vérification du token:', error);
    return false;
  }
};

/**
 * Rafraîchit le token d'accès
 * @param {Object} client - Instance Axios
 * @returns {Promise<string>} Nouveau token d'accès
 */
export const refreshAccessToken = async (client) => {
  try {
    // Récupérer le refresh token depuis le localStorage
    const refreshToken = localStorage.getItem('easySurvey.refresh_token');
    
    if (!refreshToken) {
      throw new Error('Refresh token manquant');
    }

    Logger.log('[TokenRefresh] Rafraîchissement du token en cours...');

    const response = await client.post('/api/token/refresh/', {
      refresh: refreshToken,
    });

    const { access } = response.data;

    if (!access) {
      throw new Error('Token d\'accès manquant dans la réponse');
    }

    // ✅ Stocker le nouveau token avec le refresh token s'il est fourni
    storage.setToken(access, response.data.refresh);
    Logger.log('[TokenRefresh] Token rafraîchi avec succès');

    return access;
  } catch (error) {
    Logger.error('[TokenRefresh] Erreur lors du rafraîchissement:', error);
    // ✅ Nettoyer les données d'authentification (envoie un signal de déconnexion)
    storage.clearAll();
    throw error;
  }
};

/**
 * Configure l'intercepteur de réponse pour le refresh automatique
 * @param {Object} client - Instance Axios
 */
export const setupTokenRefreshInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Si erreur 401 et pas déjà en train de rafraîchir
      if (error.response?.status === 401 && !isRefreshing) {
        // Vérifier que ce n'est pas l'endpoint de refresh lui-même
        if (originalRequest.url.includes('/api/token/refresh/')) {
          // Le refresh a échoué, nettoyer et rediriger
          Logger.warn('[TokenRefresh] Refresh token expiré, déconnexion...');
          storage.clearAll();
          // ✅ Utiliser React Router via localStorage (gérée par SecurityInitializer)
          window.location.href = '/';
          return Promise.reject(error);
        }

        isRefreshing = true;

        try {
          // Rafraîchir le token
          const newToken = await refreshAccessToken(client);

          // ✅ Mettre à jour le header Authorization
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          client.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

          // Traiter la queue des requêtes en attente
          processQueue(null, newToken);

          // Réessayer la requête originale
          return client(originalRequest);
        } catch (refreshError) {
          // Erreur lors du refresh
          Logger.error('[TokenRefresh] Erreur lors du refresh, déconnexion...');
          processQueue(refreshError, null);
          storage.clearAll();
          // ✅ Utiliser React Router via localStorage (gérée par SecurityInitializer)
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      }

      // Si en train de rafraîchir, ajouter à la queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Configure un intervalle pour rafraîchir le token avant expiration
 * @param {number} checkIntervalMinutes - Intervalle de vérification en minutes
 */
export const setupTokenRefreshInterval = (checkIntervalMinutes = 5) => {
  const intervalId = setInterval(() => {
    if (shouldRefreshToken(2)) {
      // Importer ici pour éviter les dépendances circulaires
      import('./client').then(({ default: client }) => {
        refreshAccessToken(client).catch((error) => {
          Logger.error('[TokenRefresh] Erreur lors du rafraîchissement périodique:', error);
        });
      });
    }
  }, checkIntervalMinutes * 60 * 1000);

  return intervalId;
};
